import {StateCreator} from 'zustand'

// ─── initiativeAnalytics types ───────────────────────────────────────────────
export interface IAnalyticsFilters {
  startDate: Date | null
  endDate: Date | null
  status: string
  type: string
  goal: string
}

// ─── teamView types ───────────────────────────────────────────────────────────
/** The board's filter set — one set for the whole screen.
 *
 * There used to be a `tvTaskFilters` / `tvInitiativeFilters` pair here, and
 * every action took `{isTaskView, ...}` and branched to one of them. That is
 * what made the tasks/initiatives switcher swap the user's whole selection out
 * from under them. One set, no branching.
 *
 * `people` holds email addresses: it is the only identity the board matches on,
 * and storing full user records is what forced the panel to map a selection back
 * through the org directory on every change. */
export interface ITeamFilters {
  /** Raw status labels as the active side of the switcher spells them. Kept raw
   * rather than normalised because the two sides genuinely disagree (tasks are
   * Pending/In progress/…, initiatives are New/Active/…) and a selection has to
   * survive a round trip through the switcher. */
  statuses: string[]
  goals: string[]
  people: string[]
  /** `YYYY-MM-DD` calendar day, or null when unset. Null rather than a floor
   * sentinel — the request adapter omits the key entirely instead. */
  startDate: string | null
  endDate: string | null
}

export type TTeamFilterType = 'task' | 'initiative'

interface ITeamDateRangePayload {
  startDate: string | null
  endDate: string | null
}

// ─── Combined store interface ─────────────────────────────────────────────────
export interface IAnalyticsTeamViewStore {
  // initiativeAnalytics UI state
  analyticsFilters: IAnalyticsFilters

  // initiativeAnalytics sync actions
  changeAnalyticsFilters: (params: {type: string; value: string}) => void
  resetInitiativeAnalytics: () => void

  // teamView UI state
  tvFilters: ITeamFilters
  tvCustomFields: Record<string, {name?: string}[]>
  /** Ids of the two server-side saved-filter rows. Both are kept so an explicit
   * "Save filters" can write the same set to each — they used to hold two
   * independent sets, and letting them drift would resurrect the split on the
   * next login. */
  tvFilterRowIds: Record<TTeamFilterType, string | undefined>

  // teamView sync actions
  applyTeamFilters: (
    filters: ITeamFilters,
    customFields: Record<string, {name?: string}[]>,
  ) => void
  setTeamDateFilter: (payload: ITeamDateRangePayload) => void
  hydrateTeamFilters: (
    filters: Partial<ITeamFilters>,
    customFields?: Record<string, {name?: string}[]>,
  ) => void
  setTeamFilterRowId: (type: TTeamFilterType, id?: string) => void
  clearTeamFilters: () => void
}

// ─── Initial state ────────────────────────────────────────────────────────────
const initialAnalyticsState = {
  analyticsFilters: {
    startDate: null,
    endDate: null,
    status: 'ALL',
    type: 'INITIATIVES',
    goal: 'all',
  } as IAnalyticsFilters,
}

export const EMPTY_TEAM_FILTERS: ITeamFilters = {
  statuses: [],
  goals: [],
  people: [],
  startDate: null,
  endDate: null,
}

const initialTeamViewState = {
  tvFilters: EMPTY_TEAM_FILTERS,
  tvCustomFields: {} as Record<string, {name?: string}[]>,
  tvFilterRowIds: {task: undefined, initiative: undefined} as Record<
    TTeamFilterType,
    string | undefined
  >,
}

export const createAnalyticsTeamViewSlice: StateCreator<
  IAnalyticsTeamViewStore,
  [],
  [],
  IAnalyticsTeamViewStore
> = set => ({
  ...initialAnalyticsState,
  ...initialTeamViewState,

  // ── initiativeAnalytics sync actions ─────────────────────────────────────
  changeAnalyticsFilters: ({type, value}) =>
    set(state => ({
      analyticsFilters: {...state.analyticsFilters, [type]: value},
    })),

  resetInitiativeAnalytics: () => set(initialAnalyticsState),

  // ── teamView sync actions ─────────────────────────────────────────────────
  // Replaces the set wholesale rather than accumulating, which is what lets the
  // panel stage a draft and commit it on Apply.
  applyTeamFilters: (filters, customFields) =>
    set({tvFilters: filters, tvCustomFields: customFields}),

  // The toolbar picker owns the date range and commits on its own, outside the
  // panel's Apply. Writing only the two date keys is what stops it from
  // clobbering a selection the panel is still staging.
  setTeamDateFilter: ({startDate, endDate}) =>
    set(state => ({tvFilters: {...state.tvFilters, startDate, endDate}})),

  // Server-loaded filters seed the set but must not wipe keys the payload
  // omits, so this merges rather than replaces.
  hydrateTeamFilters: (filters, customFields) =>
    set(state => ({
      tvFilters: {...state.tvFilters, ...filters},
      tvCustomFields: customFields ?? state.tvCustomFields,
    })),

  setTeamFilterRowId: (type, id) =>
    set(state => ({tvFilterRowIds: {...state.tvFilterRowIds, [type]: id}})),

  clearTeamFilters: () =>
    set({tvFilters: EMPTY_TEAM_FILTERS, tvCustomFields: {}}),
})
